import { useMemo } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

/**
 * SkyStage platform administrators.
 *
 * These users are trusted SkyStage operators who help support the owners of
 * every microapp. They are granted the same view and capabilities a microapp
 * owner has — pod logs, application configuration, secrets, backups, redeploy,
 * and manage owners — on EVERY microapp, regardless of whether they are a
 * listed owner of that app.
 *
 * The list is NOT hardcoded: it is read from `catalogApi.admin.allowedEmails`
 * in app-config.yaml (the same allowlist that already governs the Catalog API
 * admin endpoints). This means the admin set can be changed by editing config —
 * no code change or redeploy of the app bundle is required.
 *
 * This module only controls which UI is shown. The backend enforces the same
 * `catalogApi.admin.allowedEmails` list independently (see
 * `plugins/scaffolder-backend-module-quick-projects/src/utils/skystageAdmins.ts`)
 * — that backend check is the security authority for the underlying API calls.
 *
 * HOW TO UPDATE THE ADMIN LIST:
 *   Edit `catalogApi.admin.allowedEmails` in app-config.yaml.
 *   See docs/microapp-admin-access-user-guide.md for the full procedure.
 */
const ADMIN_EMAILS_CONFIG_KEY = 'catalogApi.admin.allowedEmails';

/**
 * Returns true if the given email is in the supplied SkyStage admin allowlist.
 *
 * Pure helper — the caller provides the allowlist (e.g. from
 * {@link useSkyStageAdminEmails}) so this stays usable from non-React code and
 * from the entity-authorization helpers.
 *
 * @param email - The user's email (any casing). Empty/undefined returns false.
 * @param adminEmails - Lowercased admin allowlist.
 */
export function isSkyStageAdmin(
  email: string | undefined | null,
  adminEmails: readonly string[],
): boolean {
  if (!email || adminEmails.length === 0) return false;
  return adminEmails.includes(email.trim().toLowerCase());
}

/**
 * Hook that reads the SkyStage admin allowlist from app-config
 * (`catalogApi.admin.allowedEmails`) and returns it lowercased.
 *
 * Returns an empty array when the key is absent.
 */
export function useSkyStageAdminEmails(): string[] {
  const config = useApi(configApiRef);
  return useMemo(
    () =>
      (config.getOptionalStringArray(ADMIN_EMAILS_CONFIG_KEY) ?? []).map(e =>
        e.trim().toLowerCase(),
      ),
    [config],
  );
}
